/**
 * Public, self-only waitlist lookup (esbuild entry → js/public-bundle.js).
 *
 * Standalone: does NOT import api.ts or any dashboard module. A parent proves
 * who they are with two factors — family last name + a phone or email on file
 * — by POSTing to the unauthenticated /api/public/lookup endpoint, and gets
 * back ONLY their own position `{ grouping, n, startDate }`. There is no
 * browsable board: nothing is shown until a parent looks themselves up, and no
 * other family's data is ever sent to the browser.
 */

interface LookupResult {
  grouping: string;   // 'Infants' | 'Toddlers' | 'Preschoolers'
  n: number;          // position within that grouping
  startDate: string;  // 'August 2027' or '' if unset
}

// --- Pure helpers (exported for unit tests) ---

export function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string)
  );
}

/** HTML for one matched family's position card. */
export function resultCardHtml(r: LookupResult): string {
  const grouping = escapeHtml(r.grouping || 'waitlist');
  const start = r.startDate
    ? `<div class="lookup-card-start">Requested start: <strong>${escapeHtml(r.startDate)}</strong></div>`
    : '';
  return (
    `<div class="lookup-card">` +
    `<div class="lookup-card-num">#${r.n}</div>` +
    `<div class="lookup-card-body">` +
    `<div class="lookup-card-line">on the <strong>${grouping}</strong> waitlist</div>` +
    start +
    `</div></div>`
  );
}

/** Full result-area HTML for a set of matches (0, 1, or several). */
export function resultsHtml(results: LookupResult[]): string {
  if (!results || results.length === 0) {
    return (
      `<div class="lookup-none">` +
      `<p><strong>We couldn&rsquo;t find a match.</strong></p>` +
      `<p>Double-check the last name, and that the phone or email is one we have on your ` +
      `application. Still stuck? Email ` +
      `<a href="mailto:office@commercechildrenscenter.com">office@commercechildrenscenter.com</a>.</p>` +
      `</div>`
    );
  }
  const intro = results.length > 1
    ? `<p class="lookup-intro">Here are your children&rsquo;s current spots:</p>`
    : `<p class="lookup-intro">Here&rsquo;s your current spot:</p>`;
  const note =
    `<p class="lookup-note">See something that doesn&rsquo;t look right? Email us at ` +
    `<a href="mailto:office@commercechildrenscenter.com">office@commercechildrenscenter.com</a> ` +
    `and we&rsquo;ll take a look.</p>`;
  return intro + results.map(resultCardHtml).join('') + note;
}

// --- Wiring ---

function setResult(html: string): void {
  const el = document.getElementById('lookup-result');
  if (!el) return;
  el.innerHTML = html;
  (el as HTMLElement).hidden = false;
}

async function runLookup(lastName: string, contact: string): Promise<void> {
  const btn = document.getElementById('lookup-submit') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }
  try {
    const res = await fetch('/api/public/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lastName, contact }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setResult(
        `<div class="lookup-none"><p>${escapeHtml((data && data.error) ||
          'Something went wrong. Please try again in a moment.')}</p></div>`
      );
      return;
    }
    setResult(resultsHtml((data && data.results) || []));
  } catch {
    setResult(
      `<div class="lookup-none"><p>The lookup is temporarily unavailable. Please try again in a moment.</p></div>`
    );
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Check my spot'; }
  }
}

function init(): void {
  const form = document.getElementById('lookup-form') as HTMLFormElement | null;
  if (!form) return;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const lastName = (document.getElementById('lookup-lastname') as HTMLInputElement | null)?.value || '';
    const contact = (document.getElementById('lookup-contact') as HTMLInputElement | null)?.value || '';
    if (!lastName.trim() || !contact.trim()) {
      setResult(
        `<div class="lookup-none"><p>Please enter both your last name and a phone number or email.</p></div>`
      );
      return;
    }
    void runLookup(lastName.trim(), contact.trim());
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', init);
}
